import { TRACKING_EVENT_NAME } from '@adtrackify/at-tracking-event-types';

import {
  ANALYTICS_FLOW,
} from '@/lib/analytics/types/analytics-enums';
import * as Logger from '@/lib/monitoring/logger';

import { analytics } from './analytics';
import { CustomPixelProvider } from './providers/custom-pixel';


interface SignupTrackingParams {
  email: string;
  firstName?: string;
  lastName?: string;
  flow: ANALYTICS_FLOW;
}

export function trackSuccessfulSignup(params: SignupTrackingParams): void {
  try {
    const { email, firstName, lastName, flow } = params;

    analytics.track(TRACKING_EVENT_NAME.SIGN_UP, {
      email,
      firstName,
      lastName,
      flow,
    });
  } catch (err) {
    Logger.error('Failed to track successful signup', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupStarted(params: { flow: ANALYTICS_FLOW }): void {
  try {
    analytics.track(TRACKING_EVENT_NAME.SIGNUP_STARTED, {
      flow: params.flow,
    });
  } catch (err) {
    Logger.error('Failed to track signup started', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupCompleted(params: { email: string; userId: string; flow: ANALYTICS_FLOW }): void {
  try {
    analytics.track(TRACKING_EVENT_NAME.SIGNUP_COMPLETED, {
      email: params.email,
      userId: params.userId,
      flow: params.flow,
    });
  } catch (err) {
    Logger.error('Failed to track signup completed', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupEmailInitialization(params: { email: string; pageParams: Record<string, string> }): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Email Initialization', {
      ...params.pageParams,
      email: params.email,
    });
  } catch (err) {
    Logger.error('Failed to track signup email initialization', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupEmailEntered(params: { email: string; pageParams: Record<string, string> }): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Email Entered', {
      ...params.pageParams,
      email: params.email,
    });
  } catch (err) {
    Logger.error('Failed to track signup email entered', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupEmailAlreadyExists(params: { email: string; pageParams: Record<string, string> }): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Email Already Exists', {
      ...params.pageParams,
      email: params.email,
    });
  } catch (err) {
    Logger.error('Failed to track signup email already exists', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupLoginLinkClicked(): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Clicked Login', {});
  } catch (err) {
    Logger.error('Failed to track signup login link clicked', err instanceof Error ? err : null);
  }
}

export function trackSignupRegistrationAttempt(params: { email: string; pageParams: Record<string, string> }): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Attempt Registration Form Submission', {
      ...params.pageParams,
      email: params.email,
    });
  } catch (err) {
    Logger.error('Failed to track signup registration attempt', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupUserCreated(params: {
  email: string;
  accountId: string;
  pageParams: Record<string, string>;
}): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - User Created', {
      ...params.pageParams,
      email: params.email,
      accountId: params.accountId,
    });
  } catch (err) {
    Logger.error('Failed to track signup user created', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupUserCreationFailed(params: {
  email: string;
  error: string;
  pageParams: Record<string, string>;
}): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - User Creation Failed', {
      ...params.pageParams,
      email: params.email,
      error: params.error,
    });
  } catch (err) {
    Logger.error('Failed to track signup user creation failed', err instanceof Error ? err : null, { params });
  }
}

export function trackSignupPageViewed(params: { step: string | number; pageParams: Record<string, string> }): void {
  try {
    CustomPixelProvider.fireTrack('Signup Flow - Page Viewed', {
      ...params.pageParams,
      step: params.step,
    });
  } catch (err) {
    Logger.error('Failed to track signup page viewed', err instanceof Error ? err : null, { params });
  }
}
